/**
 * YouTube URL Helper Functions
 *
 * This module handles extraction and validation of YouTube video IDs from various URL formats.
 * YouTube videos can be shared in many different formats, and this helper normalizes them all.
 */

/**
 * Extract YouTube video ID from various URL formats
 *
 * Supported formats:
 * 1. Standard watch URL: https://www.youtube.com/watch?v=dQw4w9WgXcQ
 * 2. Short URL: https://youtu.be/dQw4w9WgXcQ
 * 3. Embed URL: https://www.youtube.com/embed/dQw4w9WgXcQ
 * 4. V URL: https://www.youtube.com/v/dQw4w9WgXcQ
 * 5. YouTube Shorts: https://www.youtube.com/shorts/dQw4w9WgXcQ
 * 6. Mobile URL: https://m.youtube.com/watch?v=dQw4w9WgXcQ
 * 7. Live URL: https://www.youtube.com/live/dQw4w9WgXcQ
 * 8. With parameters: https://www.youtube.com/watch?v=dQw4w9WgXcQ&t=123s
 * 9. With playlist: https://www.youtube.com/watch?v=dQw4w9WgXcQ&list=PLxxx
 * 10. Short with timestamp: https://youtu.be/dQw4w9WgXcQ?t=123
 * 11. Just video ID: dQw4w9WgXcQ
 *
 * @param {string} url - YouTube URL in any supported format
 * @returns {string} - 11-character video ID, or empty string if invalid
 */
export function extractYouTubeId(url) {
  if (!url) return '';

  // Remove whitespace
  url = url.trim();

  // YouTube video IDs are always 11 characters: a-zA-Z0-9_-
  const patterns = [
    // Standard watch URLs: https://www.youtube.com/watch?v=VIDEO_ID
    /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,

    // Short URLs: https://youtu.be/VIDEO_ID
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,

    // Embed URLs: https://www.youtube.com/embed/VIDEO_ID
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,

    // V URLs: https://www.youtube.com/v/VIDEO_ID
    /(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,

    // User URLs: https://www.youtube.com/user/username#p/u/11/VIDEO_ID
    /(?:youtube\.com\/user\/[^\/]+#p\/u\/\d+\/)([a-zA-Z0-9_-]{11})/,

    // YouTube Shorts: https://www.youtube.com/shorts/VIDEO_ID
    /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,

    // Mobile URLs: https://m.youtube.com/watch?v=VIDEO_ID
    /(?:m\.youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,

    // Attribution URLs: https://www.youtube.com/attribution_link?...v=VIDEO_ID
    /(?:youtube\.com\/attribution_link\?.*v=)([a-zA-Z0-9_-]{11})/,

    // Live URLs: https://www.youtube.com/live/VIDEO_ID
    /(?:youtube\.com\/live\/)([a-zA-Z0-9_-]{11})/,

    // Just the video ID (11 characters)
    /^([a-zA-Z0-9_-]{11})$/
  ];

  // Try each pattern
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return '';
}

/**
 * Validate if a URL is a valid YouTube URL
 *
 * @param {string} url - YouTube URL to validate
 * @returns {boolean} - true if valid YouTube URL with extractable video ID
 */
export function isValidYouTubeUrl(url) {
  if (!url) return false;
  const videoId = extractYouTubeId(url);
  return videoId.length === 11;
}

/**
 * Get clean YouTube embed URL from any YouTube URL format
 *
 * @param {string} url - YouTube URL in any format
 * @returns {string} - Clean embed URL (https://www.youtube.com/embed/VIDEO_ID) or empty string
 */
export function getYouTubeEmbedUrl(url) {
  const videoId = extractYouTubeId(url);
  if (!videoId) return '';
  return `https://www.youtube.com/embed/${videoId}`;
}

/**
 * Get YouTube thumbnail URL
 *
 * @param {string} url - YouTube URL in any format
 * @param {string} quality - Thumbnail quality: 'default', 'hq', 'mq', 'sd', 'maxres'
 * @returns {string} - Thumbnail URL or empty string
 */
export function getYouTubeThumbnail(url, quality = 'hq') {
  const videoId = extractYouTubeId(url);
  if (!videoId) return '';

  const qualityMap = {
    'default': 'default',
    'mq': 'mqdefault',
    'hq': 'hqdefault',
    'sd': 'sddefault',
    'maxres': 'maxresdefault'
  };

  const thumbnailQuality = qualityMap[quality] || 'hqdefault';
  return `https://img.youtube.com/vi/${videoId}/${thumbnailQuality}.jpg`;
}

/**
 * Get standard YouTube watch URL from any format
 *
 * @param {string} url - YouTube URL in any format
 * @returns {string} - Standard watch URL (https://www.youtube.com/watch?v=VIDEO_ID) or empty string
 */
export function getYouTubeWatchUrl(url) {
  const videoId = extractYouTubeId(url);
  if (!videoId) return '';
  return `https://www.youtube.com/watch?v=${videoId}`;
}

/**
 * Example usage and test cases
 */
if (process.env.NODE_ENV === 'development') {
  const testUrls = [
    'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
    'https://youtu.be/dQw4w9WgXcQ',
    'https://www.youtube.com/embed/dQw4w9WgXcQ',
    'https://www.youtube.com/shorts/dQw4w9WgXcQ',
    'https://m.youtube.com/watch?v=dQw4w9WgXcQ',
    'https://www.youtube.com/watch?v=dQw4w9WgXcQ&t=123s',
    'dQw4w9WgXcQ',
    'invalid-url',
    'https://vimeo.com/123456'
  ];

  // Uncomment to test
  // testUrls.forEach(url => {
  //   console.log(`URL: ${url}`);
  //   console.log(`  Valid: ${isValidYouTubeUrl(url)}`);
  //   console.log(`  Video ID: ${extractYouTubeId(url)}`);
  //   console.log(`  Embed URL: ${getYouTubeEmbedUrl(url)}`);
  //   console.log('---');
  // });
}
